import { z } from "zod";
import { uuid } from "./schemas/common";

/**
 * Seating auto-arrange. Pure and deterministic (same input → same plan), so the API computes it, tests pin it, and
 * nothing is written until the couple applies a proposal.
 *
 * Rules, in order of strength:
 * 1. A household is never split (it is one invitation).
 * 2. "Keep apart" pairs never share a table.
 * 3. "Keep together" pairs share a table when one fits them; otherwise the proposal reports it.
 * 4. Softer affinity: same group and side (+5), same side (+2).
 * Capacity counts heads: party size plus a plus-one.
 */
export const SEATING_RULE_KIND = ["together", "apart"] as const;
export type SeatingRuleKind = (typeof SEATING_RULE_KIND)[number];

export const createSeatingRuleSchema = z.object({ guestAId: uuid, guestBId: uuid, kind: z.enum(SEATING_RULE_KIND) })
  .refine((r) => r.guestAId !== r.guestBId, { message: "Choose two different guests.", path: ["guestBId"] });
export type CreateSeatingRule = z.infer<typeof createSeatingRuleSchema>;

export interface SeatingRuleDTO { id: string; guestAId: string; guestBId: string; kind: SeatingRuleKind; createdAt: string }

export const arrangeSchema = z.object({
  includeMaybe: z.boolean().default(false),
  /** Keep guests where they already sit and only place the rest. */
  keepExisting: z.boolean().default(true),
});
export type ArrangeInput = z.infer<typeof arrangeSchema>;

export const applyArrangementSchema = z.object({
  assignments: z.array(z.object({ guestId: uuid, tableId: uuid.nullable() })).min(1).max(2000),
});
export type ApplyArrangement = z.infer<typeof applyArrangementSchema>;

export interface ArrangeGuest {
  id: string; householdId: string | null; partyCount: number; plusOneAllowed: boolean; side: string; category: string;
  rsvp: string; seatingTableId: string | null;
}
export interface ArrangeTable { id: string; number: number; capacity: number }
export interface ArrangeRule { guestAId: string; guestBId: string; kind: SeatingRuleKind }

export interface ArrangementDTO {
  assignments: { guestId: string; tableId: string | null }[];
  /** Guests who should have a seat but did not fit anywhere. */
  unseated: string[];
  /** Rules the plan could not honour (only "together" can be broken: "apart" is never violated). */
  violations: { guestAId: string; guestBId: string; kind: SeatingRuleKind }[];
  score: number;
  tables: { tableId: string; heads: number; capacity: number }[];
}

const heads = (g: ArrangeGuest) => g.partyCount + (g.plusOneAllowed ? 1 : 0);
const NEG = -1e9;

export function arrangeSeating(input: { guests: ArrangeGuest[]; tables: ArrangeTable[]; rules: ArrangeRule[]; options: ArrangeInput }): ArrangementDTO {
  const { options } = input;
  const tables = [...input.tables].sort((a, b) => a.number - b.number || a.id.localeCompare(b.id));
  const tableIds = new Set(tables.map((t) => t.id));
  const wanted = input.guests.filter((g) => g.rsvp === "accepted" || (options.includeMaybe && g.rsvp === "maybe"));
  const byId = new Map(wanted.map((g) => [g.id, g]));

  // Units: households, then merged through "together" rules (union–find) as long as the merged unit can fit a table.
  const parent = new Map<string, string>();
  const find = (k: string): string => { let r = k; while (parent.get(r) !== r) r = parent.get(r)!; parent.set(k, r); return r; };
  const unitKey = (g: ArrangeGuest) => g.householdId ?? `g:${g.id}`;
  for (const g of wanted) parent.set(unitKey(g), unitKey(g));
  const members = () => {
    const m = new Map<string, ArrangeGuest[]>();
    for (const g of wanted) { const r = find(unitKey(g)); m.set(r, [...(m.get(r) ?? []), g]); }
    return m;
  };
  const maxCap = Math.max(0, ...tables.map((t) => t.capacity));
  const rules = input.rules.filter((r) => byId.has(r.guestAId) && byId.has(r.guestBId));
  const apart = rules.filter((r) => r.kind === "apart");
  const isApart = (a: string, b: string) => apart.some((r) => (r.guestAId === a && r.guestBId === b) || (r.guestAId === b && r.guestBId === a));
  for (const r of rules.filter((x) => x.kind === "together")) {
    const ra = find(unitKey(byId.get(r.guestAId)!)), rb = find(unitKey(byId.get(r.guestBId)!));
    if (ra === rb) continue;
    const m = members();
    const merged = [...(m.get(ra) ?? []), ...(m.get(rb) ?? [])];
    const conflict = merged.some((x) => merged.some((y) => x !== y && isApart(x.id, y.id)));
    if (!conflict && merged.reduce((s, g) => s + heads(g), 0) <= maxCap) parent.set(rb, ra);
  }
  const units = [...members().entries()].map(([key, ms]) => ({ key, members: ms, heads: ms.reduce((s, g) => s + heads(g), 0) }));

  const seat = new Map<string, string | null>(); // unit key → table id
  const load = new Map(tables.map((t) => [t.id, 0]));
  const at = (tableId: string) => units.filter((u) => seat.get(u.key) === tableId);

  // Kept placements first (a unit is kept when all its members already sit at the same live table).
  if (options.keepExisting) {
    for (const u of units) {
      const ts = new Set(u.members.map((g) => g.seatingTableId));
      const only = ts.size === 1 ? [...ts][0] : null;
      if (only && tableIds.has(only) && load.get(only)! + u.heads <= tables.find((t) => t.id === only)!.capacity) {
        seat.set(u.key, only);
        load.set(only, load.get(only)! + u.heads);
      }
    }
  }

  const pairScore = (a: ArrangeGuest, b: ArrangeGuest) => {
    if (isApart(a.id, b.id)) return NEG;
    let s = 0;
    if (rules.some((r) => r.kind === "together" && ((r.guestAId === a.id && r.guestBId === b.id) || (r.guestAId === b.id && r.guestBId === a.id)))) s += 50;
    if (a.side === b.side) s += a.category === b.category ? 5 : 2;
    return s;
  };
  const affinity = (u: (typeof units)[number], tableId: string, except?: string) =>
    at(tableId).filter((o) => o.key !== except && o.key !== u.key).reduce((s, o) => s + u.members.reduce((x, a) => x + o.members.reduce((y, b) => y + pairScore(a, b), 0), 0), 0);

  // Greedy: largest units first into the table with the best affinity that has room; ties go to the fuller table.
  const order = units.filter((u) => !seat.has(u.key)).sort((a, b) => b.heads - a.heads || a.key.localeCompare(b.key));
  for (const u of order) {
    let best: string | null = null, bestScore = NEG;
    for (const t of tables) {
      if (load.get(t.id)! + u.heads > t.capacity) continue;
      const sc = affinity(u, t.id);
      if (sc <= NEG / 2) continue;
      if (best === null || sc > bestScore || (sc === bestScore && load.get(t.id)! > load.get(best)!)) { best = t.id; bestScore = sc; }
    }
    seat.set(u.key, best);
    if (best) load.set(best, load.get(best)! + u.heads);
  }

  // Bounded improvement: swap two placed, movable units between tables when both fit and the total gets better.
  const movable = units.filter((u) => seat.get(u.key) && !(options.keepExisting && u.members.every((g) => g.seatingTableId === seat.get(u.key))));
  for (let pass = 0, improved = true; improved && pass < 20; pass++) {
    improved = false;
    for (let i = 0; i < movable.length; i++) for (let j = i + 1; j < movable.length; j++) {
      const a = movable[i]!, b = movable[j]!;
      const ta = seat.get(a.key)!, tb = seat.get(b.key)!;
      if (ta === tb) continue;
      const capA = tables.find((t) => t.id === ta)!.capacity, capB = tables.find((t) => t.id === tb)!.capacity;
      if (load.get(ta)! - a.heads + b.heads > capA || load.get(tb)! - b.heads + a.heads > capB) continue;
      const before = affinity(a, ta) + affinity(b, tb);
      seat.set(a.key, tb); seat.set(b.key, ta);
      const after = affinity(a, tb, b.key) + affinity(b, ta, a.key);
      if (after > before) {
        load.set(ta, load.get(ta)! - a.heads + b.heads); load.set(tb, load.get(tb)! - b.heads + a.heads);
        improved = true;
      } else { seat.set(a.key, ta); seat.set(b.key, tb); }
    }
  }

  const tableOf = new Map<string, string | null>();
  for (const u of units) for (const g of u.members) tableOf.set(g.id, seat.get(u.key) ?? null);
  let score = 0;
  for (const t of tables) {
    const gs = wanted.filter((g) => tableOf.get(g.id) === t.id);
    for (let i = 0; i < gs.length; i++) for (let j = i + 1; j < gs.length; j++) score += pairScore(gs[i]!, gs[j]!);
  }
  return {
    assignments: wanted.map((g) => ({ guestId: g.id, tableId: tableOf.get(g.id) ?? null })),
    unseated: wanted.filter((g) => !tableOf.get(g.id)).map((g) => g.id),
    violations: rules.filter((r) => r.kind === "together" && (tableOf.get(r.guestAId) === null || tableOf.get(r.guestAId) !== tableOf.get(r.guestBId))).map((r) => ({ ...r })),
    score,
    tables: tables.map((t) => ({ tableId: t.id, heads: load.get(t.id)!, capacity: t.capacity })),
  };
}
